import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import EventPageInfo from "../../components/EventPageInfo.jsx";
import AdditionalInfo from "../../components/AdditionalInfo.jsx";
import MoreInfo from "../../components/MoreInfo.jsx";
import OrganizerCard from "../../components/OrganizerCard.jsx";
import CancellationPolicyBanner from "../../components/CancellationPolicyBanner.jsx";
import SitePolicyPrompt from "../../components/SitePolicyPrompt.jsx";
import "react-loading-skeleton/dist/skeleton.css";
import { endpoints } from "../../utils/Constants.js";
import { createGlobalStyle } from "styled-components";
import { OlympiacosTheme, MoreTheme, breakpoints } from "../../components/common/mixins.js";
import useMiniSite from "../../hooks/useMiniSite.js";

const EventPageScopedGlobalStyle = createGlobalStyle`
  footer2 {
    display: none !important;
  }

  #ui-wrap {
    min-height: 100vh !important;
  }

  @media (max-width: ${breakpoints.tablet}px) {
    #main {
      padding-bottom: 60px !important;
    }
  }
`;

import {
  destroyModal,
  renderBookingPanelLinks,
  eventGroupCode,
  nodeAliasPath,
  mainUrl,
  getLocaleSegment,
  sitePolicyState,
  backButtonText,
} from "../../utils/Utils.js";
import { mutateEventPropsData } from "../../utils/Mutates.js";

const BackButtonContainer = styled.div`
  width: 100%;
  background: #f9f9f9;
  padding-bottom: 16px;
  padding-top: 12px;

  @media (max-width: 767px) {
    display: none;
  }
`;

const BackButtonLink = styled.a`
  display: flex;
  flex-direction: row;
  gap: 4px;
  background: #f9f9f9;
  place-items: center;
  text-decoration: none !important;
  width: fit-content;

  * {
    filter: brightness(0) saturate(100%) invert(53%) sepia(15%)
      saturate(1527%) hue-rotate(88deg) brightness(101%) contrast(92%);
  }

  &:hover * {
    filter: brightness(0) saturate(100%) invert(68%) sepia(14%)
      saturate(1308%) hue-rotate(88deg) brightness(99%) contrast(91%);
    text-decoration: none;
  }

  &:active * {
    filter: brightness(0) saturate(100%) invert(32%) sepia(22%)
      saturate(1165%) hue-rotate(92deg) brightness(88%) contrast(83%);
  }

  span {
    font-size: 16px;
    font-weight: 600;
    color: #3da862;
  }

  img {
    height: 12px;
    padding: 0 8px;
  }
`;

const EventApp = () => {
  const { isOlympiacos, isPaoBC } = useMiniSite();
  const [eventProps, setEventProps] = useState([]);
  const [hasEventEnded, setHasEventEnded] = useState(false);
  const [loading, setLoading] = useState(true);

  const [organizers, setOrganizers] = useState([]);
  const [organizersLoading, setOrganizersLoading] = useState(true);

  const [media, setMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(true);

  const sitePolicyPromptRef = useRef(null);

  useEffect(() => {
    //hides mini cancellation i icons
    $("[id*=cancellationPolicyDiv]").hide();
    fetchData();
  }, []);

  useEffect(() => {
    const gateSelector = ".r_buttonPrice, .events-container__item .eb-button";
    const onClickCapture = (e) => {
      const target =
        e.target && typeof e.target.closest === "function"
          ? e.target.closest(gateSelector)
          : null;
      if (!target) return;
      const reason = sitePolicyState();
      if (!reason) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      sitePolicyPromptRef.current?.open(reason, window.location.href);
    };
    document.addEventListener("click", onClickCapture, true);
    return () => document.removeEventListener("click", onClickCapture, true);
  }, []);

  useEffect(() => {
    if (eventProps?.events && !eventProps.hasSameInfoData) {
      renderBookingPanelLinks();
      setBookingPanelLinks(eventProps?.events);
    }
  }, [eventProps]);

  const fetchData = () => {
    const code = encodeURIComponent(eventGroupCode());
    fetch(endpoints.event.getMedia, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        nodealiaspath: encodeURIComponent(nodeAliasPath()),
      }),
    })
      .then((mediaResponse) => mediaResponse.json())
      .then((media) => {
        setMedia(media);
        setMediaLoading(false);
      })
      .catch((error) => {
        console.error(`Can't fetch media!`, error);
        setMediaLoading(false);
      });
    fetch(`${endpoints.event.getEvents}?eventGroupCode=${code}`)
      .then((response) => {
        if (response.status === 204) {
          setHasEventEnded(true);
          $(".event-ended").css({
            margin: "16px 16px 24px 16px",
            "border-radius": "10px",
          });
          $(".cf").css({ "box-shadow": "0px 0px 85px rgb(0 0 0 / 7%)" });
        }
        return response.json();
      })
      .then((eventProps) => {
        setEventProps(mutateEventPropsData(eventProps));
        setHasEventEnded(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error(`Can't fetch event dates!`, error);
        setLoading(false);
      });
    fetch(endpoints.event.getProducers, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
        body: JSON.stringify({
            eventGroupCode: eventGroupCode(),
            producerId:null
        }),
    })
      .then((result) => result.json())
      .then(
        (data) => {
          setOrganizers(data);
          setOrganizersLoading(false);
        },
        (error) => {
          console.log("cant fetch organizers", error);
          setOrganizersLoading(false);
        }
      );
  };

  const setBookingPanelLinks = (eventProps) => {
    $(".moreInfo").on("click", function () {
      let link = $("<div id='modal'></div>");
      let eventId = $(this).data("eventid");
        $(this).append(link);
      renderModal(eventId, eventProps);
    });
  };

  const renderModal = (eventId, eventProps) => {
      const eventProp = eventProps.find((p) => p.eventId === eventId);
      const moreInfoLink = document.querySelector(`[data-eventid='${eventId}']`);
      const modal = moreInfoLink.querySelector("#modal");
    ReactDOM.render(
      <MoreInfo
        destroyModal={destroyModal("#modal")}
        eventId={eventId}
        eventProp={eventProp}
        key={eventId}
        loading={loading}
        />,
      modal
    );
  };

  return (
    <>
      <MoreTheme />
      <EventPageScopedGlobalStyle />
      {isOlympiacos && <OlympiacosTheme />}
      <SitePolicyPrompt
        mandatory={false}
        onClose={() => {
          window.location.href = `${mainUrl()}/${getLocaleSegment()}/tickets/`;
        }}
        ref={sitePolicyPromptRef}
      />

      {isPaoBC && (
        <BackButtonContainer>
          <BackButtonLink href={`/${getLocaleSegment()}/`}>
            <img src="/site/images/left_arrow.svg" alt="" />
            <span>{backButtonText()}</span>
          </BackButtonLink>
        </BackButtonContainer>
      )}

      <EventPageInfo
        showModals={!eventProps?.hasSameInfoData}
        nullAdditionalInfo={eventProps?.nullAdditionalInfo}
        producerCount={eventProps?.producerCount}
        hasEventEnded={hasEventEnded}
        media={media}
        mediaLoading={mediaLoading}
        venueSeatsProp={eventProps?.venueSeatsProp}
        lat={
          eventProps?.additionalInfoData &&
          eventProps?.additionalInfoData.venueLatitude
        }
        long={
          eventProps?.additionalInfoData &&
          eventProps?.additionalInfoData.venueLongitude
        }
      />

      <AdditionalInfo
        hasAdditionalInfoSection={
          eventProps?.hasAdditionalInfoSection && !hasEventEnded
        }
        eventData={eventProps?.events && eventProps?.additionalInfoData}
        loading={loading}
      />

      <CancellationPolicyBanner
        loading={loading}
        multiplePolicies={eventProps?.multipleCancellationPolicies}
        multiplePoliciesSpecific={eventProps?.multipleCancellationPoliciesSpecific}
        hasCancellationPolicySection={eventProps?.hasCancellationPolicySection}
        allowCancel={eventProps?.allowCancelProp}
        allowUpdate={eventProps?.allowUpdateProp}
        producerName={eventProps?.producerNameProp}
        cancellationPolicy={eventProps?.cancellationPolicySpecificProp}
      />

      <OrganizerCard loading={organizersLoading} organizers={organizers} />
    </>
  );
};

export default EventApp;
